import { Injectable } from '@angular/core';
import {
  AdditionalChip,
  ChipConfig,
  HeaderAndTableCellDraft,
  HeaderCell,
  MappedTableData,
  TableActionElement,
  TableCell,
  TableDraft,
  TableIconElement,
} from '@markant-platform/shared/table/model';
import { get } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class TableService {
  returnMappedTableData(genericData: unknown[], tableDraft: TableDraft): MappedTableData {
    return {
      tableVariant: tableDraft.tableVariant,
      rowLink: tableDraft.rowLink,
      disabledRowLink: tableDraft.disabledRowLink,
      rowClickControl: tableDraft.rowClickControl ?? false,
      showCheckBoxes: tableDraft.showCheckBoxes ?? false,
      showRadioButtons: tableDraft.showRadioButtons ?? false,
      sortColumnIds: tableDraft.sortColumnIds ?? [],
      sortStartColumnId: tableDraft.sortStartColumnId ?? '',
      sortStartDirection: tableDraft.sortStartDirection ?? 'desc',
      headerCells: tableDraft.headerAndTableCells
        .filter(cell => !tableDraft.hiddenColumns?.includes(cell.headerCellColumnId))
        .map(
          (item: HeaderAndTableCellDraft): HeaderCell => ({
            headerCellColumnId: item.headerCellColumnId,
            headerCellTranslationKey: item.headerCellTranslationKey,
            headerCellTitle: item.headerCellTitle,
          })
        ),
      tableRows: genericData.map(genericItem => ({
        rowLink: (genericItem as { relativeRouteForSingleRow: string }).relativeRouteForSingleRow ?? '',
        entryId: (genericItem as { id: string }).id,
        version: (genericItem as { version: number }).version ?? -1,
        object: genericItem,
        disabledRow: tableDraft.disabledRow ? tableDraft.disabledRow(genericItem) : false,
        showCheckBox: tableDraft.isShowCheckboxForRow ? tableDraft.isShowCheckboxForRow(genericItem) : true,
        showRadioButton: tableDraft.isShowRadioButtonForRow ? tableDraft.isShowRadioButtonForRow(genericItem) : true,
        tableCells: tableDraft.headerAndTableCells
          .filter(cell => !tableDraft.hiddenColumns?.includes(cell.headerCellColumnId))
          .map(
            (cell: HeaderAndTableCellDraft): TableCell => ({
              content: this.returnCellContent(genericItem, cell),
              truncate: cell.tableCellTruncate,
              contentType: cell.tableCellContentType,
              dateFormat: cell.tableCellDateFormat,
              contentIsTranslationKey: !!cell.tableCellTranslationPrefix,
              chips: this.setChipDataToCell(genericItem, cell),
              actionElements: this.returnCellActionElements(genericItem, cell),
              iconElements: this.setIconsToDataCell(genericItem, cell),
              selectionPrefix: cell.selectionPrefix,
              isEditable$: cell.isEditable$,
              isEditable: cell.isEditable,
            })
          ),
      })),
    };
  }

  setIconsToDataCell(genericItem: unknown, cell: HeaderAndTableCellDraft): TableIconElement[] | undefined {
    const { iconElementsConfigMap, tableCellContentType } = cell;
    if (iconElementsConfigMap && tableCellContentType === 'ICONS') {
      return this.returnCellIconElements(genericItem, cell, iconElementsConfigMap);
    }

    return undefined;
  }

  //Icons provided form API or another source
  //Icon colorScheme/icon/size defined by default
  returnCellIconElements(
    genericItem: unknown,
    { tableCellContentKey, iconElements }: HeaderAndTableCellDraft,
    iconElementsConfigMap: Record<string, { colorScheme: string; icon: string; size: string }>
  ): TableIconElement[] | undefined {
    if (!iconElementsConfigMap) {
      return [];
    }

    const actionsSource = get(genericItem, tableCellContentKey.split('.'));

    if (actionsSource && iconElementsConfigMap) {
      // if source is a string, create an array
      const actionsSourceArray = [].concat(actionsSource);
      // loop through icon source array and return
      return actionsSourceArray.map(
        (key: string): TableIconElement => ({
          colorScheme: iconElementsConfigMap[key]?.colorScheme || '',
          icon: iconElementsConfigMap[key]?.icon || '',
          size: iconElementsConfigMap[key]?.size || '11',
        })
      );
    }

    return iconElements;
  }

  //By default, all row action elements are visible (if added)
  //Row action elements could be skipped by using disableRowActionElementsCondition
  //Result of disableRowActionElementsCondition 'true' - skips adding actions on a row
  returnCellActionElements(genericItem: unknown, cell: HeaderAndTableCellDraft): TableActionElement[] | undefined {
    const { tableCellContentType, disableRowActionElementsCondition } = cell;

    if (disableRowActionElementsCondition && tableCellContentType === 'ACTION') {
      const disableActionElementsOnRow = disableRowActionElementsCondition(genericItem);
      if (disableActionElementsOnRow) {
        return [];
      }
    }

    return cell.actionElements;
  }

  setChipDataToCell(genericItem: unknown, cell: HeaderAndTableCellDraft): ChipConfig[] | undefined {
    const { tableCellContentType, additionalChip, chipConfigMapStatic, chipConfigMap } = cell;
    if (tableCellContentType === 'CHIP_PREDEFINED' && chipConfigMapStatic) {
      return this.returnPredefinedChips(genericItem, cell, chipConfigMapStatic);
    }

    if (tableCellContentType === 'CHIP' && chipConfigMap) {
      return this.returnChips(genericItem, cell, chipConfigMap);
    }

    if (tableCellContentType === 'CHIP_AND_STRING' && additionalChip) {
      const match = additionalChip.displayCondition(genericItem);

      return match ? this.returnChips(genericItem, additionalChip, additionalChip.chipConfigMap) : [];
    }

    return undefined;
  }

  // the following variations are currently possible:
  // - value
  // - translationPrefix + value
  // - date
  // - chip
  // - chip + value
  // - checkbox (by boolean value 'showCheckboxes' at first level of mappedTableData)
  // - buttons + value (action-elements)
  private returnCellContent(genericItem: unknown, cell: HeaderAndTableCellDraft): (string | Date)[] {
    const { tableCellContentKey, tableCellContentType } = cell;
    // getting nested values from object using lodash 'get'
    const contentSource = get(genericItem, tableCellContentKey.split('.'));

    // getting data from object etc. but only if property tableCellContentType is NOT 'CHIP' or 'SELECT' or 'CHIP_PREDEFINED'.
    // a cell displays either a string / date content or one or more chips.
    const excludedCellContentTypes = ['CHIP', 'SELECT', 'CHIP_PREDEFINED'];

    if ((contentSource || contentSource === 0) && !excludedCellContentTypes.includes(tableCellContentType)) {
      // if source is a string, create an array
      const contentSourceArray: (string | number)[] =
        typeof contentSource === 'string' || typeof contentSource === 'number' ? [contentSource] : contentSource;

      return this.returnContent(contentSourceArray, cell);
    }
    return [];
  }

  private returnContent(
    contentSourceArray: (string | number)[],
    { tableCellContentType, tableCellTranslationPrefix }: HeaderAndTableCellDraft
  ): (string | Date)[] {
    // loop through content source array and return
    return contentSourceArray.map((content: string | number): string | Date => {
      // In case we deal with numbers we need to convert them to a string
      // because otherwise the number zero would not be displayed.
      if (typeof content === 'number') {
        content = content.toString();
      }

      const contentCellDate = tableCellContentType === 'DATE' ? new Date(content) : content || '-';

      // here the tableCellTranslationKey and the property value
      // get merged to get a valid translation-key-string for transloco
      return tableCellTranslationPrefix && content ? `${tableCellTranslationPrefix}.${content}` : contentCellDate;
    });
  }

  //Chip with already translated text provided form API or another source
  //Chip color/icon defined by default
  private returnPredefinedChips(
    genericItem: unknown,
    cell: HeaderAndTableCellDraft | AdditionalChip<unknown>,
    chipConfigMapStatic: { color: string; icon: string }
  ): ChipConfig[] {
    // getting/creating source array for chips
    if (!chipConfigMapStatic) {
      return [];
    }

    const chipSource = get(genericItem, cell.tableCellContentKey);

    if (chipSource) {
      // if source is a string, create an array
      const chipSourceArray = [].concat(chipSource);

      // loop through chip source array and return
      return chipSourceArray.map(
        (key: string): ChipConfig => ({
          colorScheme: chipConfigMapStatic?.color || '',
          labelTranslationKey: key,
          readonly: true,
          icon: chipConfigMapStatic?.icon || '',
        })
      );
    }
    return [];
  }

  private returnChips(
    genericItem: unknown,
    cell: HeaderAndTableCellDraft | AdditionalChip<unknown>,
    chipStateMap: Record<string, { color: string; icon: string }>
  ): ChipConfig[] {
    // getting/creating source array for chips
    const chipSource = get(genericItem, cell.tableCellContentKey.split('.'));

    if (chipSource) {
      // if source is a string, create an array
      const chipSourceArray = [].concat(chipSource);

      // loop through chip source array and return
      return chipSourceArray.map(
        (key: string): ChipConfig => ({
          colorScheme: chipStateMap[key]?.color || '',
          labelTranslationKey: `${cell.tableCellTranslationPrefix}.${key}`,
          readonly: true,
          icon: chipStateMap[key]?.icon || '',
        })
      );
    }
    return [];
  }
}
